import type { SupervisorContact } from '@saniplus/contracts';
import { Notice, User, cn, decorativeIcon } from '@saniplus/ui';
import { useTranslation } from 'react-i18next';

/**
 * EVERY RECOVERY ENDS IN A PERSON.
 *
 * `W8-recovery/JUDGEMENT.md` §4: what makes the winning template honest is
 * that each dead end finishes with a named supervisor and a number, not with
 * "contact your supervisor" shouted into a corridor. §6.1.7 adds the condition
 * that used to make this impossible — `GET /me/context` had no supervisor
 * field — and the Phase 3 batch added `supervisorContacts[]`, with `phone`
 * nullable because `users.phone` is.
 *
 * So there are exactly three renderings and no fourth:
 *
 * - a name **and** a number → a real `tel:` link a gloved thumb can hit;
 * - a name with `phone: null` → the same block, not a link, saying there is
 *   no number on file. Never a dead `tel:`;
 * - no contact at all → a `Notice`, never an empty button.
 *
 * ## Two emphases, because two of the eight conditions are not dead ends
 *
 * On a BLOCKING screen the supervisor is the only way forward, and the judged
 * artboards draw the call as the filled primary key
 * (`VariantA-aucun-quart`, `-quart-non-ferme`, `-telephone-non-reconnu`,
 * `-synchro-refusee`). On the two INFORMATIONAL screens — offline-at-punch and
 * GPS-denied — the primary is « Continuer » or « Pointer quand même », and the
 * same artboards drop the call to a quiet blue line
 * (`VariantA-hors-ligne`, `-position-refusee`). Rendering it filled on those
 * two puts two primaries on one surface, which the product-wide rule forbids
 * and which tells a worker whose punch is safely queued that they need to
 * phone somebody. `emphasis` is that distinction and nothing else: the same
 * three renderings, the same `tel:`, the same accessible name.
 */
export interface SupervisorActionProps {
  readonly contacts: readonly SupervisorContact[] | undefined;
  /** Overrides the second line — « Il peut autoriser ce téléphone tout de suite ». */
  readonly subLabel?: string;
  /**
   * `key` (default) — the filled primary, for a screen with no other way out.
   * `quiet` — a text-weight line, for the two informational screens where
   * continuing is the primary.
   */
  readonly emphasis?: 'key' | 'quiet';
  readonly className?: string;
}

const BASE_CLASSES = 'flex min-h-touch-lg w-full items-center gap-3 rounded-control px-4 py-3';

const KEY_CLASSES = [
  BASE_CLASSES,
  'bg-action text-action-fg shadow-key-sm',
  'transition-colors dur-state ease-out',
  'hover:bg-action-hover active:translate-y-px active:bg-action-press active:shadow-key-sm-pressed',
];

const QUIET_CLASSES = [
  BASE_CLASSES,
  'justify-center text-link',
  'transition-colors dur-state ease-out hover:bg-surface-sunk',
];

export function SupervisorAction({
  contacts,
  subLabel,
  emphasis = 'key',
  className,
}: SupervisorActionProps) {
  const { t } = useTranslation('punch');
  const contact = contacts?.[0];
  const shell = emphasis === 'quiet' ? QUIET_CLASSES : KEY_CLASSES;

  if (contact === undefined) {
    return (
      <Notice
        tone="neutral"
        title={t('supervisor.contact')}
        description={t('supervisor.none')}
        {...(className === undefined ? {} : { className })}
      />
    );
  }

  const name = `${contact.firstName} ${contact.lastName}`;

  /*
   * The second line WRAPS; it does not truncate, and only the phone is mono.
   *
   * It used to be one `truncate` span in `num`, and at 390 px a real Quebec
   * name spent the whole line: « Jean-Philippe Rousseau-Lemay · Auc… ». The
   * clipped half is the half that matters — whether there is a number to call
   * at all — on the one screen whose entire job is to end in a person. Two
   * lines is what the key's `min-h-touch-lg` was already tall enough for.
   *
   * Mono is for facts (`DESIGN.md`), so it goes on the phone number and not on
   * a person's name: a proportional name is easier to read at arm's length,
   * and a monospaced one looks like a serial number.
   */
  const second =
    subLabel !== undefined ? (
      <>{subLabel}</>
    ) : contact.phone === null ? (
      // No dimming: `action-fg` has no muted partner on the filled key, and
      // "there is no number on file" is the fact the worker most needs, not a
      // footnote to it.
      <>{`${t('supervisor.nameOnly', { name })} · ${t('supervisor.noPhone')}`}</>
    ) : (
      <>
        {name}
        <span aria-hidden="true">{' · '}</span>
        <span className="num">{contact.phone}</span>
      </>
    );

  /*
   * Quiet is ONE line, not a smaller version of two.
   *
   * The artboards that drop this to a link show « Contacter mon superviseur »
   * alone: the name and the number belong on the screen where calling is the
   * next thing you do, and repeating them under a text link is a paragraph
   * pretending to be a control. The `tel:` still carries the number.
   */
  const body =
    emphasis === 'quiet' ? (
      <span className="text-body font-medium">{t('supervisor.contact')}</span>
    ) : (
      <>
        <User {...decorativeIcon('xl')} className="shrink-0" />
        <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-start">
          <span className="text-title-2">{t('supervisor.contact')}</span>
          <span className="w-full text-balance break-words text-body-compact font-medium">
            {second}
          </span>
        </span>
      </>
    );

  if (contact.phone === null) {
    return (
      <div
        data-testid="supervisor-action"
        /*
         * It looks like the key beside it because it carries the same fact,
         * but it is NOT pressable — there is no number to dial — so it gives
         * up every affordance that promises a press: the bevel, the pressed
         * translate, the hover fill. A block that depresses under a thumb and
         * then does nothing is the screen lying at the exact moment a worker
         * needs to believe it.
         */
        className={cn(
          shell,
          'cursor-default shadow-none active:translate-y-0 active:shadow-none',
          emphasis === 'quiet' ? '' : 'hover:bg-action',
          className,
        )}
      >
        {body}
      </div>
    );
  }

  return (
    <a
      data-testid="supervisor-action"
      href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
      aria-label={`${t('supervisor.contact')} · ${name}`}
      className={cn(shell, className)}
    >
      {body}
    </a>
  );
}
